import React from 'react';
import { MdOutlineOpenInNew, MdOutlineCheck, MdOutlineClose, MdOutlineHelpOutline } from 'react-icons/md';
import { Pill, Chip, FactGrid, Note, Disclosure, SubHeading, StatusPill } from './ui';
import DataTable from '../charts/DataTable';
import { fmtNum, fmtAcres, fmtDate, titleCase, safeArray, isNum, DASH } from '../format';

const FLAG_LABELS = {
  single_family: 'Single-family',
  two_family: 'Two-family',
  multi_family: 'Multi-family',
  commercial: 'Commercial',
  industrial: 'Industrial',
  adu_local: 'ADU (local)',
  adu_state: 'ADU (state)',
  short_term_rentals: 'Short-term rentals',
};

export function flagTone(value) {
  if (value === true) return 'good';
  if (value === false) return 'muted';
  return 'neutral';
}

function FlagPill({ label, value }) {
  const Icon = value === true ? MdOutlineCheck : value === false ? MdOutlineClose : MdOutlineHelpOutline;
  const text = value === true ? 'permitted' : value === false ? 'not permitted' : 'unknown';
  return (
    <span className={`le-flag le-flag-${flagTone(value)}`} title={`${label}: ${text}`}>
      <Icon size={13} /> {label}
    </span>
  );
}

function controlValue(c) {
  if (!c || c.value === null || c.value === undefined) return null;
  const numeric = isNum(c.numeric) && String(c.numeric) === String(c.value).replace(/,/g, '');
  if (numeric && c.unit) return `${fmtNum(c.numeric, Number.isInteger(c.numeric) ? 0 : 1)} ${c.unit}`;
  return c.value;
}

/**
 * Zoning & entitlements block rendered inside the Property card: district,
 * use permissions, the dimensional controls that size a project, the parcel
 * record, and the signals worth acting on.
 */
export default function ZoningSection({ zoning, status }) {
  if (!zoning && !status) return null;
  if (!zoning) {
    const label = status?.status === 'not_available' ? 'Zoning provider not configured on this backend.'
      : status?.status === 'not_found' ? 'No zoning district matched this address or point.'
        : 'Zoning lookup did not return data.';
    return (
      <>
        <SubHeading right={<StatusPill status={status} />}>Zoning &amp; entitlements</SubHeading>
        <Note tone="muted">{label}{safeArray(status?.warnings).length ? ` ${status.warnings.join(' ')}` : ''}</Note>
      </>
    );
  }

  const district = zoning.district || {};
  const source = zoning.source || {};
  const plu = zoning.permitted_land_uses || {};
  const flags = plu.flags || {};
  const keyControls = safeArray(zoning.key_controls);
  const byKey = Object.fromEntries(keyControls.map((c) => [c.key, c]));
  const parcel = zoning.parcel || null;
  const signals = safeArray(zoning.signals);
  const tags = zoning.plu_tags || {};
  const tagCount = Object.values(tags).reduce((n, list) => n + safeArray(list).length, 0);
  const controlsRows = safeArray(zoning.controls);

  const facts = [
    { label: 'Max height', value: [controlValue(byKey.max_building_height_ft), controlValue(byKey.max_building_height_stories)].filter(Boolean).join(' · ') || null },
    { label: 'Max FAR', value: controlValue(byKey.max_far) },
    { label: 'Max density', value: controlValue(byKey.max_density_du_per_acre) },
    { label: 'Max lot coverage', value: controlValue(byKey.max_lot_coverage_percent) },
    { label: 'Max impervious', value: controlValue(byKey.max_impervious_percent) },
    { label: 'Min open space', value: controlValue(byKey.min_open_space_percent) },
    { label: 'Min lot area', value: controlValue(byKey.min_lot_area_sq_ft) },
    { label: 'Min lot width', value: controlValue(byKey.min_lot_width_ft) },
    { label: 'Min lot depth', value: controlValue(byKey.min_lot_depth_ft) },
    { label: 'Front setback', value: controlValue(byKey.min_front_setback_ft) },
    { label: 'Side setback', value: [controlValue(byKey.min_side_setback_ft), controlValue(byKey.min_side_setback_both_ft) ? `${controlValue(byKey.min_side_setback_both_ft)} both` : null].filter(Boolean).join(' · ') || null },
    { label: 'Rear setback', value: controlValue(byKey.min_rear_setback_ft) },
  ];

  return (
    <>
      <SubHeading right={status && status.status !== 'ok' ? <StatusPill status={status} /> : null}>Zoning &amp; entitlements</SubHeading>

      <div className="le-zone-head">
        <span className="le-zone-code" title={district.zone_type ? `${district.zone_type}${district.zone_sub_type ? ` · ${district.zone_sub_type}` : ''}` : undefined}>{district.zone_code || DASH}</span>
        <div className="le-zone-titles">
          <div className="le-zone-name">{district.zone_name || 'Zoning district'}</div>
          <div className="le-zone-meta">
            {[district.zone_type, district.zone_sub_type].filter(Boolean).join(' · ')}
            {source.city_name ? ` · ${source.city_name}` : ''}
            {source.last_updated ? ` · ordinance digitized ${fmtDate(source.last_updated)}` : ''}
          </div>
        </div>
        {district.ordinance_link ? (
          <a className="le-link le-zone-link" href={district.ordinance_link} target="_blank" rel="noopener noreferrer">Ordinance <MdOutlineOpenInNew size={12} /></a>
        ) : null}
      </div>
      {district.zone_guide ? <p className="le-zone-guide">{district.zone_guide}</p> : null}

      <div className="le-flag-row" aria-label="Use permissions">
        {Object.keys(FLAG_LABELS).map((key) => <FlagPill key={key} label={FLAG_LABELS[key]} value={flags[key]} />)}
      </div>

      {signals.length ? (
        <div className="le-signal-list">
          {zoning.lookup?.fallback_reason === 'address_match_rejected' ? (
            <Note tone="warning">
              <strong>Address match rejected.</strong> The zoning source matched this address to APN {zoning.lookup.rejected_match?.apn || DASH}{zoning.lookup.rejected_match?.address ? ` at "${zoning.lookup.rejected_match.address}"` : ''}, but {zoning.lookup.rejected_match?.problem || 'it did not agree with the geocoded point'}. The parcel that contains the geocoded point is shown instead; confirm the APN before relying on it.
            </Note>
          ) : null}
          {signals.map((s) => <Note key={s.code} tone={s.tone === 'good' ? 'info' : s.tone || 'info'}>{s.message}</Note>)}
        </div>
      ) : null}

      <div className="le-zone-grid">
        <div>
          <div className="le-zone-block-title">Dimensional controls</div>
          <FactGrid items={facts} />
          <div className="le-footnotes muted">
            Standardized values first; ordinance wording kept where the code says "see text". NA means the ordinance sets no value.
          </div>
        </div>
        <div>
          <div className="le-zone-block-title">Permitted uses</div>
          {safeArray(plu.as_of_right).length ? (
            <>
              <div className="le-zone-uses-label">As of right</div>
              <ul className="le-plain-list">{plu.as_of_right.map((u) => <li key={u}>{u}</li>)}</ul>
            </>
          ) : <div className="le-school-none">No as-of-right uses listed</div>}
          {safeArray(plu.conditional_uses).length ? (
            <>
              <div className="le-zone-uses-label">Conditional</div>
              <ul className="le-plain-list">{plu.conditional_uses.map((u) => <li key={u}>{u}</li>)}</ul>
            </>
          ) : null}
        </div>
      </div>

      {parcel ? (
        <>
          <div className="le-zone-block-title">Parcel record</div>
          <FactGrid
            items={[
              { label: 'APN', value: parcel.apn ? <span className="mono">{parcel.apn}</span> : null },
              { label: 'Parcel area', value: isNum(parcel.area_sq_ft) ? `${fmtNum(parcel.area_sq_ft)} sq ft · ${fmtAcres(parcel.area_acres)}` : null, hint: parcel.area_reported?.unit ? `Reported as ${parcel.area_reported.value} ${parcel.area_reported.unit}` : undefined },
              { label: 'Assessor land use', value: parcel.assessor_land_use ? <Pill tone={signals.some((s) => s.code === 'ASSESSOR_USE_DIFFERS_FROM_ZONING') ? 'warning' : 'neutral'}>{parcel.assessor_land_use}</Pill> : null, hint: 'Current use per assessor — not what zoning allows' },
              { label: 'Parcel address', value: parcel.address },
              { label: 'Outline', value: parcel.boundary_geojson ? 'Drawn on the map' : 'No geometry returned', muted: !parcel.boundary_geojson },
              { label: 'Parcels returned', value: parcel.candidates_returned > 1 ? `${parcel.candidates_returned} (nearest shown)` : null, hidden: !(parcel.candidates_returned > 1) },
            ]}
          />
        </>
      ) : (
        <Note tone="muted">No parcel record came back for this location, so there is no outline to draw.</Note>
      )}

      {tagCount ? (
        <Disclosure summary={`${tagCount} land-use tags across ${Object.keys(tags).length} categories`}>
          <div className="le-tag-groups">
            {Object.entries(tags).map(([category, list]) => (
              <div key={category} className="le-tag-group">
                <span className="le-tag-group-name">{titleCase(category)}</span>
                <span className="le-chip-row">{safeArray(list).map((t) => <Chip key={t}>{t}</Chip>)}</span>
              </div>
            ))}
          </div>
        </Disclosure>
      ) : null}

      {controlsRows.length ? (
        <Disclosure summary={`All ${controlsRows.length} controls the ordinance returned`}>
          <DataTable
            compact
            columns={[
              { key: 'category', label: 'Category', format: (v) => titleCase(String(v).replace(/_standard$/, '')) },
              { key: 'field', label: 'Field', format: (v) => String(v).replace(/_/g, ' ') },
              { key: 'basis', label: 'Basis', format: (v) => (v === 'standardized' ? 'Standardized' : 'Ordinance text') },
              { key: 'raw', label: 'Value', format: (v) => (v === null || v === undefined ? DASH : String(v)) },
            ]}
            rows={controlsRows}
          />
        </Disclosure>
      ) : null}

      <div className="le-footnotes muted">
        Source: municipal zoning ordinance, digitized{source.city_name ? ` · ${source.city_name}` : ''}{zoning.lookup?.address_used ? ` · looked up as "${zoning.lookup.address_used}"` : ''}. Not a substitute for a zoning verification letter or a survey.
      </div>
    </>
  );
}
